import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type FormEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Icon, type IconName } from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
    buildExportFilename,
    buildUserDataExport,
    downloadJsonFile,
} from '../lib/exportUserData';

type Feedback = { tone: 'success' | 'error'; message: string } | null;

interface SectionMeta {
    id: 'profile' | 'security' | 'preferences' | 'privacy';
    label: string;
    icon: IconName;
}

const SECTIONS: SectionMeta[] = [
    { id: 'profile', label: 'Profil', icon: 'user' },
    { id: 'security', label: 'Sécurité', icon: 'shield' },
    { id: 'preferences', label: 'Préférences', icon: 'settings' },
    { id: 'privacy', label: 'Données & confidentialité', icon: 'download' },
];

export function AccountPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const rawUser = user?.raw ?? null;

    const meta = useMemo(
        () => (rawUser?.user_metadata ?? {}) as Record<string, unknown>,
        [rawUser],
    );

    const hasPasswordProvider =
        (rawUser?.app_metadata?.providers as string[] | undefined)?.includes('email') ??
        rawUser?.app_metadata?.provider === 'email';

    /* ---------- Navigation active (scroll-spy) ---------- */
    const [activeSection, setActiveSection] = useState<SectionMeta['id']>('profile');
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
                if (visible[0]) {
                    setActiveSection(visible[0].target.id as SectionMeta['id']);
                }
            },
            {
                rootMargin: '-120px 0px -55% 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            },
        );
        SECTIONS.forEach((s) => {
            const el = sectionRefs.current[s.id];
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const goToSection = (id: SectionMeta['id']) => {
        const el = sectionRefs.current[id];
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    /* ---------- Profil ---------- */
    const [displayName, setDisplayName] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileFeedback, setProfileFeedback] = useState<Feedback>(null);

    useEffect(() => {
        if (!rawUser) return;
        setDisplayName(
            (meta.display_name as string | undefined) ??
                (meta.full_name as string | undefined) ??
                (meta.name as string | undefined) ??
                '',
        );
        setAvatarUrl(
            (meta.avatar_url as string | undefined) ??
                (meta.picture as string | undefined) ??
                '',
        );
    }, [rawUser, meta]);

    const handleProfileSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!rawUser) return;
        setProfileFeedback(null);
        setSavingProfile(true);
        try {
            const { error } = await supabase.auth.updateUser({
                data: {
                    display_name: displayName.trim() || null,
                    avatar_url: avatarUrl.trim() || null,
                },
            });
            if (error) throw error;
            setProfileFeedback({ tone: 'success', message: 'Profil mis à jour.' });
        } catch (err) {
            setProfileFeedback({
                tone: 'error',
                message: err instanceof Error ? err.message : 'Mise à jour impossible.',
            });
        } finally {
            setSavingProfile(false);
        }
    };

    /* ---------- Sécurité ---------- */
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);

    const handlePasswordSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setPasswordFeedback(null);
        if (newPassword.length < 8) {
            setPasswordFeedback({
                tone: 'error',
                message: 'Le mot de passe doit contenir au moins 8 caractères.',
            });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordFeedback({
                tone: 'error',
                message: 'Les deux mots de passe ne correspondent pas.',
            });
            return;
        }
        setSavingPassword(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;
            setNewPassword('');
            setConfirmPassword('');
            setPasswordFeedback({
                tone: 'success',
                message: 'Mot de passe mis à jour.',
            });
        } catch (err) {
            setPasswordFeedback({
                tone: 'error',
                message: err instanceof Error ? err.message : 'Mise à jour impossible.',
            });
        } finally {
            setSavingPassword(false);
        }
    };

    /* ---------- Préférences ---------- */
    const [locale, setLocale] = useState<'fr' | 'en'>('fr');
    const [emailNotifs, setEmailNotifs] = useState(true);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [prefsFeedback, setPrefsFeedback] = useState<Feedback>(null);

    useEffect(() => {
        if (!rawUser) return;
        const l = meta.locale as string | undefined;
        setLocale(l === 'en' ? 'en' : 'fr');
        const notifs = meta.notifications as { email?: boolean } | undefined;
        setEmailNotifs(notifs?.email !== false);
    }, [rawUser, meta]);

    const handlePrefsSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!rawUser) return;
        setPrefsFeedback(null);
        setSavingPrefs(true);
        try {
            const { error } = await supabase.auth.updateUser({
                data: {
                    locale,
                    notifications: { email: emailNotifs },
                },
            });
            if (error) throw error;
            setPrefsFeedback({ tone: 'success', message: 'Préférences enregistrées.' });
        } catch (err) {
            setPrefsFeedback({
                tone: 'error',
                message: err instanceof Error ? err.message : 'Mise à jour impossible.',
            });
        } finally {
            setSavingPrefs(false);
        }
    };

    /* ---------- Export & suppression ---------- */
    const [exporting, setExporting] = useState(false);
    const [exportFeedback, setExportFeedback] = useState<Feedback>(null);

    const handleExport = async () => {
        if (!rawUser) return;
        setExportFeedback(null);
        setExporting(true);
        try {
            const data = await buildUserDataExport(rawUser);
            downloadJsonFile(data, buildExportFilename());
            setExportFeedback({ tone: 'success', message: 'Export téléchargé.' });
        } catch (err) {
            setExportFeedback({
                tone: 'error',
                message: err instanceof Error ? err.message : 'Export impossible.',
            });
        } finally {
            setExporting(false);
        }
    };

    const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm'>('idle');
    const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteFeedback, setDeleteFeedback] = useState<Feedback>(null);

    const handleDelete = async () => {
        if (deleteConfirmInput.trim().toUpperCase() !== 'SUPPRIMER') {
            setDeleteFeedback({
                tone: 'error',
                message: 'Veuillez saisir exactement « SUPPRIMER ».',
            });
            return;
        }
        setDeleteFeedback(null);
        setDeleting(true);
        try {
            const { error } = await supabase.rpc('delete_my_account');
            if (error) throw error;
            await logout();
            navigate('/login', { replace: true });
            setTimeout(() => {
                window.location.href = '/';
            }, 300);
        } catch (err) {
            setDeleteFeedback({
                tone: 'error',
                message: err instanceof Error ? err.message : 'Suppression impossible.',
            });
            setDeleting(false);
        }
    };

    const setSectionRef = (id: SectionMeta['id']) => (el: HTMLElement | null) => {
        sectionRefs.current[id] = el;
    };

    return (
        <DashboardLayout>
            <main className="dash-main account-main">
                <header className="account-header">
                    <span className="dash-eyebrow">Paramètres</span>
                    <h1>Mon compte</h1>
                    <p>
                        Gérez votre profil, votre sécurité et l'usage de vos
                        renseignements personnels.
                    </p>
                </header>

                <div className="account-layout">
                    {/* ---------- SIDEBAR ---------- */}
                    <aside className="account-nav" aria-label="Sections du compte">
                        <nav>
                            {SECTIONS.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`account-nav-link${
                                        activeSection === s.id ? ' is-active' : ''
                                    }`}
                                    onClick={() => goToSection(s.id)}
                                    aria-current={activeSection === s.id ? 'true' : undefined}
                                >
                                    <Icon name={s.icon} size={15} />
                                    <span>{s.label}</span>
                                </button>
                            ))}
                        </nav>

                        <div className="account-nav-user">
                            {avatarUrl ? (
                                <img
                                    src={avatarUrl}
                                    alt=""
                                    className="account-nav-avatar"
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display =
                                            'none';
                                    }}
                                />
                            ) : (
                                <span
                                    className="account-nav-avatar account-nav-avatar-fallback"
                                    aria-hidden
                                >
                                    {getInitials(displayName, user?.email)}
                                </span>
                            )}
                            <div className="account-nav-user-info">
                                <strong>
                                    {displayName || user?.email?.split('@')[0] || '—'}
                                </strong>
                                <span>{user?.email}</span>
                            </div>
                        </div>
                    </aside>

                    {/* ---------- CONTENU ---------- */}
                    <div className="account-content">
                        {/* ========== PROFIL ========== */}
                        <section
                            id="profile"
                            ref={setSectionRef('profile')}
                            className="account-section"
                            aria-labelledby="acc-profile-title"
                        >
                            <div className="account-section-head">
                                <h2 id="acc-profile-title">Profil</h2>
                                <p>
                                    Ces informations apparaissent à côté de votre nom dans
                                    Quotio.
                                </p>
                            </div>

                            <form className="account-form" onSubmit={handleProfileSubmit}>
                                <div className="account-row">
                                    <div className="account-row-label">
                                        <label htmlFor="acc-name">Nom d'affichage</label>
                                        <small>Utilisé dans la navbar et les exports.</small>
                                    </div>
                                    <div className="account-row-input">
                                        <input
                                            id="acc-name"
                                            type="text"
                                            value={displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                            placeholder="Ex. Morgan Chappe"
                                            autoComplete="name"
                                        />
                                    </div>
                                </div>

                                <div className="account-row">
                                    <div className="account-row-label">
                                        <label htmlFor="acc-avatar">URL de l'avatar</label>
                                        <small>
                                            Image publique (https). Upload direct prévu
                                            prochainement.
                                        </small>
                                    </div>
                                    <div className="account-row-input">
                                        <input
                                            id="acc-avatar"
                                            type="url"
                                            value={avatarUrl}
                                            onChange={(e) => setAvatarUrl(e.target.value)}
                                            placeholder="https://…"
                                        />
                                    </div>
                                </div>

                                <div className="account-row">
                                    <div className="account-row-label">
                                        <label htmlFor="acc-email">Courriel</label>
                                        <small>
                                            Le changement de courriel nécessite une
                                            re-vérification (bientôt).
                                        </small>
                                    </div>
                                    <div className="account-row-input">
                                        <input
                                            id="acc-email"
                                            type="email"
                                            value={user?.email ?? ''}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>

                                <FormFooter
                                    feedback={profileFeedback}
                                    submitLabel="Enregistrer"
                                    submitIcon="save"
                                    loading={savingProfile}
                                    loadingLabel="Enregistrement…"
                                />
                            </form>
                        </section>

                        {/* ========== SÉCURITÉ ========== */}
                        <section
                            id="security"
                            ref={setSectionRef('security')}
                            className="account-section"
                            aria-labelledby="acc-security-title"
                        >
                            <div className="account-section-head">
                                <h2 id="acc-security-title">Sécurité</h2>
                                <p>
                                    Gérez votre mot de passe et vos méthodes de connexion.
                                </p>
                            </div>

                            {hasPasswordProvider === false ? (
                                <div className="account-callout">
                                    <Icon name="key" size={16} />
                                    <p>
                                        Vous vous connectez avec un fournisseur externe
                                        (Google). La gestion du mot de passe se fait
                                        directement depuis votre compte Google.
                                    </p>
                                </div>
                            ) : (
                                <form
                                    className="account-form"
                                    onSubmit={handlePasswordSubmit}
                                >
                                    <div className="account-row">
                                        <div className="account-row-label">
                                            <label htmlFor="acc-new-pwd">
                                                Nouveau mot de passe
                                            </label>
                                            <small>Au moins 8 caractères.</small>
                                        </div>
                                        <div className="account-row-input">
                                            <div className="account-input-group">
                                                <input
                                                    id="acc-new-pwd"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(e) =>
                                                        setNewPassword(e.target.value)
                                                    }
                                                    placeholder="••••••••"
                                                    autoComplete="new-password"
                                                />
                                                <button
                                                    type="button"
                                                    className="account-input-icon"
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    aria-label={
                                                        showPassword
                                                            ? 'Masquer le mot de passe'
                                                            : 'Afficher le mot de passe'
                                                    }
                                                >
                                                    <Icon
                                                        name={showPassword ? 'eye-off' : 'eye'}
                                                        size={16}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="account-row">
                                        <div className="account-row-label">
                                            <label htmlFor="acc-confirm-pwd">
                                                Confirmation
                                            </label>
                                            <small>Doit correspondre exactement.</small>
                                        </div>
                                        <div className="account-row-input">
                                            <input
                                                id="acc-confirm-pwd"
                                                type={showPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) =>
                                                    setConfirmPassword(e.target.value)
                                                }
                                                placeholder="••••••••"
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>

                                    <FormFooter
                                        feedback={passwordFeedback}
                                        submitLabel="Mettre à jour le mot de passe"
                                        submitIcon="key"
                                        loading={savingPassword}
                                        loadingLabel="Mise à jour…"
                                    />
                                </form>
                            )}
                        </section>

                        {/* ========== PRÉFÉRENCES ========== */}
                        <section
                            id="preferences"
                            ref={setSectionRef('preferences')}
                            className="account-section"
                            aria-labelledby="acc-prefs-title"
                        >
                            <div className="account-section-head">
                                <h2 id="acc-prefs-title">Préférences</h2>
                                <p>Langue, notifications et comportement par défaut.</p>
                            </div>

                            <form className="account-form" onSubmit={handlePrefsSubmit}>
                                <div className="account-row">
                                    <div className="account-row-label">
                                        <label htmlFor="acc-locale">Langue</label>
                                        <small>
                                            Une partie de l'interface est encore en français.
                                        </small>
                                    </div>
                                    <div className="account-row-input">
                                        <select
                                            id="acc-locale"
                                            value={locale}
                                            onChange={(e) =>
                                                setLocale(e.target.value as 'fr' | 'en')
                                            }
                                        >
                                            <option value="fr">Français</option>
                                            <option value="en">English (bientôt)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="account-row">
                                    <div className="account-row-label">
                                        <label htmlFor="acc-notifs">
                                            Notifications par courriel
                                        </label>
                                        <small>
                                            Recevoir un courriel à chaque nouvelle demande sur
                                            vos générateurs.
                                        </small>
                                    </div>
                                    <div className="account-row-input account-row-toggle">
                                        <label className="account-switch" htmlFor="acc-notifs">
                                            <input
                                                id="acc-notifs"
                                                type="checkbox"
                                                checked={emailNotifs}
                                                onChange={(e) =>
                                                    setEmailNotifs(e.target.checked)
                                                }
                                            />
                                            <span
                                                className="account-switch-slider"
                                                aria-hidden
                                            />
                                        </label>
                                    </div>
                                </div>

                                <FormFooter
                                    feedback={prefsFeedback}
                                    submitLabel="Enregistrer"
                                    submitIcon="save"
                                    loading={savingPrefs}
                                    loadingLabel="Enregistrement…"
                                />
                            </form>
                        </section>

                        {/* ========== DONNÉES & CONFIDENTIALITÉ ========== */}
                        <section
                            id="privacy"
                            ref={setSectionRef('privacy')}
                            className="account-section"
                            aria-labelledby="acc-privacy-title"
                        >
                            <div className="account-section-head">
                                <h2 id="acc-privacy-title">Données & confidentialité</h2>
                                <p>
                                    Exercez vos droits au titre de la Loi 25 et du RGPD.
                                </p>
                            </div>

                            <div className="account-row account-row-action">
                                <div className="account-row-label">
                                    <label>Exporter mes données</label>
                                    <small>
                                        Fichier JSON contenant votre profil, vos
                                        organisations, vos générateurs et toutes les demandes
                                        reçues.
                                    </small>
                                </div>
                                <div className="account-row-input">
                                    <button
                                        type="button"
                                        className="btn-ghost account-btn"
                                        onClick={handleExport}
                                        disabled={exporting}
                                    >
                                        {exporting ? (
                                            <>
                                                <Icon
                                                    name="loader"
                                                    size={15}
                                                    className="icon-spin"
                                                />
                                                Préparation…
                                            </>
                                        ) : (
                                            <>
                                                <Icon name="download" size={15} />
                                                Télécharger
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <FeedbackRow feedback={exportFeedback} />

                            <div className="account-danger">
                                <div className="account-danger-head">
                                    <Icon name="alert-triangle" size={16} />
                                    <div>
                                        <h3>Supprimer mon compte</h3>
                                        <p>
                                            Cette action est{' '}
                                            <strong>irréversible</strong>. Elle supprime
                                            votre compte, les organisations dont vous êtes
                                            l'unique propriétaire, ainsi que tous les
                                            générateurs et demandes associés. Les
                                            organisations partagées restent intactes, votre
                                            accès y est simplement retiré.
                                        </p>
                                    </div>
                                </div>

                                {deleteStep === 'idle' ? (
                                    <div className="account-danger-actions">
                                        <button
                                            type="button"
                                            className="btn-danger account-btn"
                                            onClick={() => {
                                                setDeleteFeedback(null);
                                                setDeleteStep('confirm');
                                            }}
                                        >
                                            <Icon name="trash" size={15} />
                                            Supprimer mon compte
                                        </button>
                                    </div>
                                ) : (
                                    <div className="account-danger-confirm">
                                        <label htmlFor="acc-delete-confirm">
                                            Pour confirmer, saisissez le mot{' '}
                                            <strong>SUPPRIMER</strong>.
                                        </label>
                                        <input
                                            id="acc-delete-confirm"
                                            type="text"
                                            value={deleteConfirmInput}
                                            onChange={(e) =>
                                                setDeleteConfirmInput(e.target.value)
                                            }
                                            placeholder="SUPPRIMER"
                                            autoComplete="off"
                                        />
                                        <FeedbackRow feedback={deleteFeedback} />
                                        <div className="account-danger-actions">
                                            <button
                                                type="button"
                                                className="btn-ghost account-btn"
                                                onClick={() => {
                                                    setDeleteStep('idle');
                                                    setDeleteConfirmInput('');
                                                    setDeleteFeedback(null);
                                                }}
                                                disabled={deleting}
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-danger account-btn"
                                                onClick={handleDelete}
                                                disabled={deleting}
                                            >
                                                {deleting ? (
                                                    <>
                                                        <Icon
                                                            name="loader"
                                                            size={15}
                                                            className="icon-spin"
                                                        />
                                                        Suppression…
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon name="trash" size={15} />
                                                        Confirmer la suppression
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </main>
        </DashboardLayout>
    );
}

/* ================================================================
   Sous-composants locaux
   ================================================================ */

interface FormFooterProps {
    feedback: Feedback;
    submitLabel: string;
    submitIcon: IconName;
    loading: boolean;
    loadingLabel: string;
}

function FormFooter({
    feedback,
    submitLabel,
    submitIcon,
    loading,
    loadingLabel,
}: FormFooterProps) {
    return (
        <div className="account-form-footer">
            <FeedbackRow feedback={feedback} />
            <button
                type="submit"
                className="btn-primary account-btn"
                disabled={loading}
            >
                {loading ? (
                    <>
                        <Icon name="loader" size={15} className="icon-spin" />
                        {loadingLabel}
                    </>
                ) : (
                    <>
                        <Icon name={submitIcon} size={15} />
                        {submitLabel}
                    </>
                )}
            </button>
        </div>
    );
}

function FeedbackRow({ feedback }: { feedback: Feedback }) {
    if (!feedback) return <span className="account-feedback-spacer" />;
    return (
        <div
            className={`account-feedback account-feedback-${feedback.tone}`}
            role={feedback.tone === 'error' ? 'alert' : 'status'}
        >
            <Icon
                name={feedback.tone === 'success' ? 'check' : 'alert-triangle'}
                size={14}
            />
            <span>{feedback.message}</span>
        </div>
    );
}

function getInitials(name?: string | null, email?: string | null): string {
    if (name && name.trim()) {
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase() || '?';
    }
    if (email) return email[0]?.toUpperCase() ?? '?';
    return '?';
}
